import {
  HostInputMessage,
  INPUT_CHANNEL_LABEL,
  parseMessage,
  TRANSFER_BUFFER_LOW_BYTES,
  TRANSFER_CHANNEL_LABEL,
  type IceServer,
  type ViewerInputMessage,
} from '@viewer/protocol';
import type { IceCandidatePayload, PeerState } from '../tauri/host';

export interface ViewerPeerOptions {
  iceServers: IceServer[];
  relayOnly: boolean;
  onIceCandidate(candidate: IceCandidatePayload): void;
  onState(state: PeerState): void;
  onTrack(stream: MediaStream): void;
  onInputOpen(open: boolean): void;
  onInputMessage(message: HostInputMessage): void;
  onTransferOpen(): void;
  onTransferBytes(bytes: Uint8Array): void;
}

export interface LinkSummary {
  /** 'relay' quando algum lado usa TURN; 'direct' quando é P2P (host/srflx/prflx). */
  path: 'direct' | 'relay';
  localType: string;
  remoteType: string;
  protocol: string;
  /** RTT medido pelo ICE, em ms. */
  rttMs: number | null;
  bytesReceived: number;
}

interface CandidatePairStats {
  type: 'candidate-pair';
  id: string;
  state?: string;
  nominated?: boolean;
  localCandidateId: string;
  remoteCandidateId: string;
  currentRoundTripTime?: number;
  bytesReceived?: number;
}

interface CandidateStats {
  candidateType?: string;
  protocol?: string;
  relayProtocol?: string;
}

export function summarizeLink(stats: RTCStatsReport): LinkSummary | null {
  let selectedId: string | undefined;
  let fallback: CandidatePairStats | undefined;
  stats.forEach((report: { type: string; selectedCandidatePairId?: string } & Record<string, unknown>) => {
    if (report.type === 'transport' && report.selectedCandidatePairId) {
      selectedId = report.selectedCandidatePairId;
    }
    if (report.type === 'candidate-pair') {
      const pair = report as unknown as CandidatePairStats;
      if (pair.nominated && pair.state === 'succeeded') fallback = pair;
    }
  });
  const pair = (selectedId ? (stats.get(selectedId) as CandidatePairStats | undefined) : undefined) ?? fallback;
  if (!pair) return null;

  const local = stats.get(pair.localCandidateId) as CandidateStats | undefined;
  const remote = stats.get(pair.remoteCandidateId) as CandidateStats | undefined;
  const localType = local?.candidateType ?? 'desconhecido';
  const remoteType = remote?.candidateType ?? 'desconhecido';
  return {
    path: localType === 'relay' || remoteType === 'relay' ? 'relay' : 'direct',
    localType,
    remoteType,
    protocol: local?.relayProtocol ?? local?.protocol ?? 'udp',
    rttMs: pair.currentRoundTripTime === undefined ? null : Math.round(pair.currentRoundTripTime * 1000),
    bytesReceived: pair.bytesReceived ?? 0,
  };
}

/** O Chromium só toca Opus em estéreo quando a própria resposta pede `stereo=1`; sem isso mistura em mono. */
export function preferStereoOpus(sdp: string): string {
  const payloadType = /^a=rtpmap:(\d+) opus\/48000\/2/im.exec(sdp)?.[1];
  if (!payloadType) return sdp;
  return sdp.replace(new RegExp(`^(a=fmtp:${payloadType} (?![^\\r\\n]*stereo=)[^\\r\\n]*)`, 'm'), '$1;stereo=1');
}

/**
 * Lado Viewer da conexão WebRTC: recebe vídeo e som do Host e envia mouse/teclado
 * pelo DataChannel "input" e arquivos pelo DataChannel "transfer" (criados pelo Host).
 */
export class ViewerPeer {
  private readonly pc: RTCPeerConnection;
  private input: RTCDataChannel | null = null;
  /** Referência mantida: se o navegador a perder, o canal fecha. */
  private transfer: RTCDataChannel | null = null;
  private readonly tracks: MediaStreamTrack[] = [];
  private closed = false;

  constructor(private readonly options: ViewerPeerOptions) {
    this.pc = new RTCPeerConnection({
      iceServers: options.iceServers.map(({ urls, username, credential }) => ({
        urls,
        ...(username !== undefined ? { username } : {}),
        ...(credential !== undefined ? { credential } : {}),
      })),
      iceTransportPolicy: options.relayOnly ? 'relay' : 'all',
      bundlePolicy: 'max-bundle',
    });

    this.pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      options.onIceCandidate({
        candidate: event.candidate.candidate,
        sdpMid: event.candidate.sdpMid,
        sdpMLineIndex: event.candidate.sdpMLineIndex,
      });
    };

    this.pc.onconnectionstatechange = () => {
      options.onState(this.pc.connectionState as PeerState);
    };

    this.pc.ontrack = (event) => {
      // Latência mínima: pede ao decodificador para não acumular jitter buffer.
      const receiver = event.receiver as RTCRtpReceiver & { jitterBufferTarget?: number | null };
      if ('jitterBufferTarget' in receiver) receiver.jitterBufferTarget = 0;
      // Tela e som chegam em streams separados (sem sincronia labial); o Viewer junta os dois.
      this.tracks.push(event.track);
      options.onTrack(new MediaStream(this.tracks));
    };

    this.pc.ondatachannel = (event) => {
      if (event.channel.label === INPUT_CHANNEL_LABEL) this.attachInput(event.channel);
      else if (event.channel.label === TRANSFER_CHANNEL_LABEL) this.attachTransfer(event.channel);
    };
  }

  /** Aplica a oferta do Host e devolve o SDP da resposta. */
  async acceptOffer(sdp: string): Promise<string> {
    await this.pc.setRemoteDescription({ type: 'offer', sdp });
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription({ type: 'answer', sdp: preferStereoOpus(answer.sdp ?? '') });
    return this.pc.localDescription?.sdp ?? answer.sdp ?? '';
  }

  async addIceCandidate(candidate: IceCandidatePayload): Promise<void> {
    if (!candidate.candidate) return;
    await this.pc.addIceCandidate({
      candidate: candidate.candidate,
      sdpMid: candidate.sdpMid,
      sdpMLineIndex: candidate.sdpMLineIndex,
    });
  }

  sendInput(message: ViewerInputMessage): boolean {
    if (!this.input || this.input.readyState !== 'open') return false;
    this.input.send(JSON.stringify(message));
    return true;
  }

  get inputOpen(): boolean {
    return this.input?.readyState === 'open';
  }

  /** Canal binário "transfer", só quando já está aberto. */
  transferChannel(): RTCDataChannel | null {
    return this.transfer?.readyState === 'open' ? this.transfer : null;
  }

  getStats(): Promise<RTCStatsReport> {
    return this.pc.getStats();
  }

  /** Par de candidatos ICE em uso: diz se a mídia vai direta ou por TURN. */
  async linkSummary(): Promise<LinkSummary | null> {
    if (this.closed) return null;
    const stats = await this.pc.getStats();
    return summarizeLink(stats);
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.input?.close();
    this.transfer?.close();
    this.pc.close();
  }

  private attachInput(channel: RTCDataChannel) {
    this.input = channel;
    channel.onopen = () => this.options.onInputOpen(true);
    channel.onclose = () => this.options.onInputOpen(false);
    channel.onmessage = (event) => {
      const parsed = parseMessage(HostInputMessage, event.data as string);
      if (parsed.ok) this.options.onInputMessage(parsed.value);
      else console.warn('mensagem inválida do host', parsed.error);
    };
  }

  private attachTransfer(channel: RTCDataChannel) {
    this.transfer = channel;
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = TRANSFER_BUFFER_LOW_BYTES;
    channel.onopen = () => this.options.onTransferOpen();
    channel.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) this.options.onTransferBytes(new Uint8Array(event.data));
    };
  }
}
